import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import * as Yup from "yup";
import orgApi from "../../api/org_api";
import {
  Button,
  CustomInput,
  Label,
  Modal,
  SearchInput,
  Table,
  Text,
} from "../../component/common";
import PaginationButtons from "../../component/PaginationButtons";
import { Style } from "../../utils/Style";
type UserProp = { setLoading: Function };
interface TeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  mobile: string;
  mobile_country_code: string;
  team_name: string;
}

const UserManagement: React.FC<UserProp> = ({
  setLoading,
}: UserProp): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [phErrorMsg, setPhErrorMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const org = JSON.parse(localStorage.getItem("user__details")?.toString()!);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const { paginatedData, totalPages } = useMemo(() => {
    const filtered =
      team && team.length > 0
        ? team.filter((x: TeamMember) => {
            const searchTerm = search.toLowerCase();
            return (
              x.email.toLowerCase().includes(searchTerm) ||
              `${x.first_name} ${x.last_name}`
                .toLowerCase()
                .includes(searchTerm)
            );
          })
        : [];

    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    return {
      paginatedData: paginated,
      totalPages,
      filteredData: filtered,
    };
  }, [team, search, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  const formik = useFormik({
    initialValues: {
      email: "",
      team_name: "",
      first_name: "",
      last_name: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email Id is required")
        .email("Invalid Email"),
      first_name: Yup.string().required("First name is required"),
      team_name: Yup.string().required("Team name is required"),
    }),
    validateOnBlur: true,
    onSubmit: async (values) => {
      var ph;
      if (
        phone !== undefined &&
        countryCode !== "" &&
        countryCode !== undefined
      ) {
        ph = phone.replace("+" + countryCode, "");
        if (ph !== "") {
        } else {
          setPhErrorMessage("Please enter the valid Phone number");
          return;
        }
      } else {
        setPhErrorMessage("Please enter the valid Phone number");
        return;
      }

      setLoading(true);
      const reqD = {
        email: values.email.toLowerCase(),
        mobile: ph,
        first_name: values.first_name,
        last_name: values.last_name,
        team_name: values.team_name,
        mobile_country_code: countryCode,
      };

      try {
        const res = await orgApi.addRec(org.id, reqD);
        if (res.data.success) {
          setShowModal(false);
          toast.success("Invite sent successfully");
          getTeam();
          handleCancel();
        } else {
          toast.error(res.data.msg);
        }
      } catch (error) {
        toast.error("Failed to send invite");
      } finally {
        setLoading(false);
      }
    },
  });

  const addInterviewer = () => {
    formik.resetForm();
    setShowModal(true);
  };

  useEffect(() => {
    getTeam();
  }, []);

  const getTeam = async () => {
    setLoading(true);
    try {
      const res = await orgApi.getRec(org.id);
      if (res.data.success) {
        setTeam(res.data.teams || []);
      }
    } catch (error) {
      toast.error("Failed to fetch team data");
    } finally {
      setLoading(false);
    }
  };

  function handlechange(e: any) {
    setPhErrorMessage("");
    setPhone(e);
    if (e !== undefined && countryCode !== "") {
      const ph = e.replace("+" + countryCode, "");
      !ph && setPhErrorMessage("Please enter the valid Phone number");
    } else {
      setPhErrorMessage("Please enter the valid Phone number");
    }
  }

  function handleBlur(e: any) {
    setPhErrorMessage("");
    const len = e.target.value.split(" ").length;
    const c = e.target.value.split(" ");
    const cc = c[0].split("+");
    setCountryCode(cc[1]);
    const ph = e.target.value.substring(e.target.value.indexOf(" ") + 1);
    if (len > 1 && ph !== "") {
    } else {
      setPhErrorMessage("Please enter the valid Phone number");
    }
  }

  const handleCancel = () => {
    setShowModal(false);
    formik.resetForm();
    setPhErrorMessage("");
    setPhone("");
    setCountryCode("");
  };

  const getTableData = () => {
    const Columns = [
      { label: "S.No", accessor: "s_no" },
      { label: "Name", accessor: "name" },
      { label: "Email", accessor: "email" },
      { label: "Contact Number", accessor: "contact_number" },
      { label: "Team", accessor: "team_name" },
    ];
    const Data =
      paginatedData.length > 0
        ? paginatedData.map((x: TeamMember, index: number) => ({
            s_no: (
              <Text key={x.id}>{currentPage * itemsPerPage + index + 1}</Text>
            ),
            name: (
              <Text key={x.id}>{`${x.first_name} ${x.last_name}`.trim()}</Text>
            ),
            email: <Text>{x.email}</Text>,
            contact_number: (
              <Text>
                {x.mobile_country_code
                  ? `+${x.mobile_country_code} ${x.mobile}`
                  : "-"}
              </Text>
            ),
            team_name: <Text>{x.team_name ? x.team_name : "-"}</Text>,
          }))
        : [];
    return {
      Columns,
      Data,
    };
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between">
          <SearchInput
            placeholder="Search with Email or Name"
            value={search}
            onChange={(e) => setSearch(e)}
            onCancel={() => setSearch("")}
          />
          <Button onClick={() => addInterviewer()} text="+ Add" />
        </div>
        <Table
          columns={getTableData().Columns}
          data={getTableData().Data}
          isStickyHeader
          containerHeight="h-[calc(100vh-197px)]"
        />
        {totalPages > 1 && (
          <div className="mt-4">
            <PaginationButtons
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}
      </div>

      <Modal onClose={handleCancel} title="Add Recruiter" isOpen={showModal}>
        <form onSubmit={formik.handleSubmit}>
          <div className="grid  md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1">
            <CustomInput
              placeholder="First Name"
              label="First Name"
              type="text"
              name="first_name"
              required
              value={formik.values.first_name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              isError={
                !!formik.touched.first_name && !!formik.errors.first_name
              }
              errorMessage={formik.errors.first_name}
            />
            <CustomInput
              placeholder="Last Name"
              label="Last Name"
              type="text"
              name="last_name"
              value={formik.values.last_name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
            <CustomInput
              placeholder="Emailnnn"
              label="Email"
              type="email"
              name="email"
              required
              value={formik.values.email}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              isError={!!formik.touched.email && !!formik.errors.email}
              errorMessage={formik.errors.email}
            />
            <div className="">
              <Label text="Phone" required />
              <PhoneInput
                defaultCountry="IN"
                international
                countryCallingCodeEditable={false}
                initialValueFormat="national"
                name="phone"
                value={phone}
                onBlur={handleBlur}
                onChange={handlechange}
                className={phErrorMsg !== "" ? Style.iError : Style.iInput}
                autoComplete="off"
              />
              {phErrorMsg && (
                <div className="error text-red-500 text-[7px]">
                  {phErrorMsg}
                </div>
              )}
            </div>
            <CustomInput
              placeholder="Team Name"
              label="Team Name"
              type="text"
              name="team_name"
              required
              value={formik.values.team_name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              isError={!!formik.touched.team_name && !!formik.errors.team_name}
              errorMessage={formik.errors.team_name}
            />
          </div>
          <div className="gap-2 mt-3 sm:flex md:flex lg:flex justify-end text-sm 2xl:text-lg">
            <Button onClick={handleCancel} type="button" text={"Cancel"} />
            <Button type="submit" text={"Invite"} />
          </div>
        </form>
      </Modal>
    </>
  );
};

export default UserManagement;
